import { EventEmitter } from 'events';
import {
  IMapEditorPlugin,
  PluginMetadata,
  PluginCapabilities,
  PluginContext,
  MapInteractionEvent,
  MapDragEvent,
  MapHoverEvent,
  MapOperation,
  MapOperationResult,
  PreviewResult,
  ValidationResult,
  HotkeyDefinition
} from '../types/PluginInterfaces';

/**
 * Base implementation for MapEditor plugins
 * Provides common functionality and simplifies plugin development
 */
export abstract class BasePlugin extends EventEmitter implements IMapEditorPlugin {
  protected context!: PluginContext;
  protected isInitialized = false;
  protected isActive = false;
  protected registeredHotkeys: string[] = [];

  constructor(
    public readonly metadata: PluginMetadata,
    public readonly capabilities: PluginCapabilities
  ) {
    super();
  }

  // Lifecycle management
  public async initialize(context: PluginContext): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    this.context = context;
    
    try {
      // Register hotkeys if plugin requires them
      if (this.capabilities.requiresHotkeys) {
        await this.registerHotkeys();
      }

      // Register pointer events if plugin requires them
      if (this.capabilities.requiresPointerEvents) {
        this.registerPointerEvents();
      }

      // Plugin-specific initialization
      await this.onInitialize();
      
      this.isInitialized = true;
      this.emit('initialized');
    } catch (error) {
      throw error;
    }
  }

  public async activate(): Promise<void> {
    if (!this.isInitialized) {
      throw new Error(`Plugin ${this.metadata.id} must be initialized before activation`);
    }

    if (this.isActive) {
      return;
    }

    try {
      // Force synchronization of brush settings when plugin activates
      if (this.context.brushSettingsBridge) {
        this.context.brushSettingsBridge.synchronize();
      }
      
      await this.onActivate();
      this.isActive = true;
      this.emit('activated');
      
      // Set this plugin as the active handler in MapPointerManager
      this.context.mapPointers.setActiveHandler(this.metadata.id);
      this.log(`Plugin activated and set as active handler: ${this.metadata.id}`);
      
      // Update status bar
      this.context.statusBarService.setStatus({
        tool: this.metadata.name,
        message: `${this.metadata.name} tool activated`
      });
    } catch (error) {
      throw error;
    }
  }

  public async deactivate(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    try {
      await this.onDeactivate();
      this.isActive = false;
      this.emit('deactivated');
      
      // Clear any previews
      this.context.previewManager.hidePreview();
      
      // Reset cursor
      this.context.mapPointers.resetCursor();
    } catch (error) {
      throw error;
    }
  }

  public async dispose(): Promise<void> {
    try {
      if (this.isActive) {
        await this.deactivate();
      }

      // Unregister all hotkeys registered by this plugin
      this.context.hotkeyManager.unregisterPlugin(this.metadata.id);
      
      // Unregister pointer events
      if (this.capabilities.requiresPointerEvents) {
        this.context.mapPointers.unregisterHandler(this.metadata.id);
      }

      // Plugin-specific cleanup
      await this.onDispose();
      
      this.isInitialized = false;
      this.emit('disposed');
      this.removeAllListeners();
    } catch (error) {
      throw error;
    }
  }

  // Hotkey management helpers
  protected registerHotkey(key: string, description: string, handler: (event: KeyboardEvent) => Promise<boolean> | boolean, priority = 50): boolean {
    try {
      // Validate parameters
      if (!key || typeof key !== 'string') {
        return false;
      }
      if (!description || typeof description !== 'string') {
        return false;
      }
      if (!handler || typeof handler !== 'function') {
        return false;
      }

      // Additional validation
      if (!this.metadata.id) {
        return false;
      }

      if (!this.context || !this.context.hotkeyManager) {
        return false;
      }

    const hotkey: HotkeyDefinition = {
      key,
      description,
      category: 'tool',
      priority,
      pluginId: this.metadata.id,
      handler
    };

      const success = this.context.hotkeyManager.register(hotkey);
    if (success) {
      this.registeredHotkeys.push(key);
    }
    return success;
    } catch (error) {
      return false;
    }
  }

  protected unregisterHotkey(key: string): boolean {
    this.context.hotkeyManager.unregister(key, this.metadata.id);
      const index = this.registeredHotkeys.indexOf(key);
      if (index > -1) {
        this.registeredHotkeys.splice(index, 1);
      return true;
    }
    return false;
  }

  // Pointer event registration
  protected registerPointerEvents(): void {
    
    this.context.mapPointers.registerHandler(this.metadata.id, {
      onPointerDown: this.handlePointerDown.bind(this),
      onPointerMove: this.handlePointerMove.bind(this),
      onPointerUp: this.handlePointerUp.bind(this),
      onPointerWheel: this.handlePointerWheel.bind(this)
    });
    
  }

  // Centralized cursor management
  protected requestCursorControl(cursor: string, priority?: number): boolean {
    if (this.context.cursorManager) {
      return this.context.cursorManager.requestCursorControl(this.metadata.id, cursor, priority);
    }
    // Fallback to legacy method
    this.context.mapPointers.setActiveCursor(cursor);
    return true;
  }

  protected releaseCursorControl(): void {
    if (this.context.cursorManager) {
      this.context.cursorManager.releaseCursorControl(this.metadata.id);
    } else {
      // Fallback to legacy method
      this.context.mapPointers.resetCursor();
    }
  }

  // Cursor highlighting management
  protected enableCursorHighlight(): void {
    if (this.context.cursorHighlightManager) {
      this.context.cursorHighlightManager.show();
    }
  }

  protected disableCursorHighlight(): void {
    if (this.context.cursorHighlightManager) {
      this.context.cursorHighlightManager.hide();
    }
  }

  protected setBrushSettings(size: number, shape: 'square' | 'circle' = 'square'): void {
    if (this.context.brushSettingsBridge) {
      this.context.brushSettingsBridge.updateSettings({ size, shape });
    } else if (this.context.brushSettingsManager) {
      this.context.brushSettingsManager.updateSettings({ size, shape });
    }
    if (this.context.cursorHighlightManager) {
      this.context.cursorHighlightManager.setBrushSettings(size, shape);
    }
  }

  protected setHighlightStyle(color: number = 0x00ff00, alpha: number = 0.8, fillAlpha: number = 0.1): void {
    if (this.context.cursorHighlightManager) {
      this.context.cursorHighlightManager.setHighlightStyle(this.metadata.id, color, alpha, fillAlpha);
    }
  }

  // Debugging helpers
  protected log(message: string, data?: any): void {
  }

  protected warn(message: string, data?: any): void {
  }

  protected error(message: string, data?: any): void {
  }

  // Default pointer event handlers (can be overridden)
  protected async handlePointerDown(event: MapInteractionEvent): Promise<boolean> {
    
    if (!this.isActive) {
      return false;
    }
    
    const result = await this.onMapClick?.(event) ?? false;
    return result;
  }

  protected async handlePointerMove(event: MapInteractionEvent): Promise<boolean> {
    
    if (!this.isActive) {
      return false;
    }
    
    if (event.type === 'drag') {
      const result = await this.onMapDrag?.(event as MapDragEvent) ?? false;
      return result;
    } else {
      // Create a proper hover event
      const hoverEvent: MapHoverEvent = {
        type: 'hover',
        worldX: event.worldX,
        worldY: event.worldY,
        tileX: event.tileX,
        tileY: event.tileY,
        pointer: event.pointer,
        layer: event.layer
      };
      const result = await this.onMapHover?.(hoverEvent) ?? false;
      return result;
    }
  }

  protected async handlePointerUp(event: MapInteractionEvent): Promise<boolean> {
    if (!this.isActive) return false;
    return this.onMapClick?.(event) ?? false;
  }

  protected async handlePointerWheel(event: WheelEvent): Promise<boolean> {
    if (!this.isActive) return false;
    return this.onMouseWheel?.(event) ?? false;
  }

  // Utility methods for common operations
  protected showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
    this.context.notificationService.show(message, type);
  }

  protected updateStatusBar(status: Partial<any>): void {
    this.context.statusBarService.setStatus(status);
  }

  protected broadcastEvent(eventName: string, data: any): void {
    this.context.broadcastEvent(`${this.metadata.id}:${eventName}`, data);
  }

  protected getPlugin<T extends IMapEditorPlugin>(pluginId: string): T | null {
    return this.context.getPlugin<T>(pluginId);
  }

  // Abstract methods that must be implemented by subclasses
  protected abstract onInitialize(): Promise<void>;
  protected abstract onActivate(): Promise<void>;
  protected abstract onDeactivate(): Promise<void>;
  protected abstract onDispose(): Promise<void>;

  // Optional methods that can be overridden
  protected async registerHotkeys(): Promise<void> {
    // Override to register plugin-specific hotkeys
  }

  // Default implementations for event handlers (can be overridden)
  public async onMapClick?(event: MapInteractionEvent): Promise<boolean> {
    return false;
  }

  public async onMapDrag?(event: MapDragEvent): Promise<boolean> {
    return false;
  }

  public async onMapHover?(event: MapHoverEvent): Promise<boolean> {
    return false;
  }

  public async onKeyDown?(event: KeyboardEvent): Promise<boolean> {
    return false;
  }

  public async onKeyUp?(event: KeyboardEvent): Promise<boolean> {
    return false;
  }

  public async onMouseWheel?(event: WheelEvent): Promise<boolean> {
    return false;
  }

  // Operation methods (can be overridden)
  public canHandle?(operation: MapOperation): boolean {
    return false;
  }

  public async execute?(operation: MapOperation): Promise<MapOperationResult> {
    throw new Error(`Plugin ${this.metadata.id} does not support operation execution`);
  }

  public async preview?(operation: MapOperation): Promise<PreviewResult> {
    return { visible: false };
  }

  public async validate?(operation: MapOperation): Promise<ValidationResult> {
    return { valid: true };
  }

  // Getters
  public get pluginId(): string {
    return this.metadata.id;
  }

  public get isPluginActive(): boolean {
    return this.isActive;
  }

  public get isPluginInitialized(): boolean {
    return this.isInitialized;
  }
} 